/**
 * Construcción del payload del evento `purchase` para GA4 / GTM.
 *
 * Contexto del bug que resuelve (2026-09):
 *   La etiqueta "Evento GA4 - purchase" de GTM lee `value` desde
 *   {{data.purchase.originalCost}}. En el camino de Stripe y PayPal el objeto
 *   que se empujaba venía de la cookie `vx.plan`, que segun quién la escribió
 *   último puede tener la forma del plan de catálogo (`originalCost`) o la de
 *   la suscripción activa del usuario (`planPrice`, escrita por las páginas de
 *   perfil/billing). Cuando le toca la segunda forma, `originalCost` queda
 *   undefined y GA4 registra el purchase con valor 0.
 *
 * Reglas de precedencia para el importe, en orden. `value_source` dice cuál
 * aplicó, para poder auditarlo en GA4:
 *   1. `trial_auth` — la retención de la prueba gratis, que Stripe no captura:
 *      sale SIN importe. No es un cobro, así que ni el `amount` del intent ni
 *      el precio del plan son ingreso.
 *   2. `payment_intent` — el `amount` del PaymentIntent (centavos + `currency`):
 *      el monto realmente cobrado, la única fuente autoritativa del front.
 *   3. `plan` — el objeto de plan, aceptando las variantes de nombre.
 *   4. `missing` — sin importe. Se etiqueta para poder medir el agujero en GA4
 *      en lugar de que pase inadvertido.
 */

const CENTS_PER_UNIT = 100;

// Stripe crea la autorización de la prueba gratis con `capture_method: manual`
// y esta descripción: retiene el importe para validar la tarjeta y no lo
// captura nunca. Un `succeeded` sí significa que el dinero se movió, así que
// sólo los estados sin capturar cuentan como retención.
const TRIAL_AUTH_DESCRIPTION = 'free_trial_auth';
const UNCAPTURED_STATUSES = ['requires_capture', 'canceled'];
const PRICE_FIELDS = ['originalCost', 'planPrice', 'cost', 'price'];
const NAME_FIELDS = ['name', 'planName'];
const ID_FIELDS = ['id', 'planId'];

const toNumber = (raw) => {
  if (raw === null || raw === undefined || raw === '') return undefined;
  const parsed = typeof raw === 'string' ? parseFloat(raw.replace(',', '.')) : raw;
  return Number.isFinite(parsed) ? parsed : undefined;
};

const firstField = (plan, fields, transform = (v) => v) => {
  if (!plan || typeof plan !== 'object') return undefined;
  for (const field of fields) {
    const value = transform(plan[field]);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

// Un importe de 0 (o negativo) no es cobrable, así que no corta la cadena de
// respaldo: si `originalCost` viene en 0 todavía puede haber un `planPrice`
// usable más abajo. Filtrar acá dentro es lo que hace que PRICE_FIELDS sea una
// cadena de respaldo y no sólo un orden de precedencia.
const toChargeableAmount = (raw) => {
  const value = toNumber(raw);
  return value !== undefined && value > 0 ? value : undefined;
};

export const planValue = (plan) => firstField(plan, PRICE_FIELDS, toChargeableAmount);

/**
 * ¿Es la retención de una prueba gratis, y no un cobro? El importe retenido es
 * distinto de cero, así que publicarlo como `value` inventa un ingreso.
 *
 * @param {object=} paymentIntent PaymentIntent de Stripe ya recuperado
 */
export const isTrialAuthorization = (paymentIntent) =>
  paymentIntent?.description === TRIAL_AUTH_DESCRIPTION &&
  UNCAPTURED_STATUSES.includes(paymentIntent.status);

// VEX-6451: el webhook de Stripe manda `purchase` a GA4 por Measurement
// Protocol, con el id del cargo como transaction_id y cubriendo también las
// renovaciones. Si el front mandara el suyo (con el id del PaymentIntent o de
// la sesión de Checkout), cada primera compra contaría doble: GA4 no deduplica
// ids distintos. PayPal y BlueSnap no los mide el backend, así que siguen acá.
const BACKEND_TRACKED_PROVIDERS = ['stripe'];

/**
 * ¿El `purchase` de este proveedor lo manda el backend? Un proveedor
 * desconocido devuelve false: perder la compra es peor que no atribuirla.
 *
 * @param {string=} provider 'stripe' | 'paypal' | 'bluesnap'
 */
export const isPurchaseTrackedByBackend = (provider) =>
  BACKEND_TRACKED_PROVIDERS.includes(provider);

export const planName = (plan) => firstField(plan, NAME_FIELDS);
export const planId = (plan) => firstField(plan, ID_FIELDS);

/**
 * @param {object}  options
 * @param {object=} options.plan           plan de catálogo o suscripción
 * @param {object=} options.paymentIntent  PaymentIntent de Stripe ya recuperado
 * @param {string=} options.transactionId  id de transacción si lo da el proveedor
 * @param {string=} options.provider       'stripe' | 'paypal' | 'bluesnap'
 */
export const buildPurchasePayload = ({ plan, paymentIntent, transactionId, provider } = {}) => {
  // La retención de la prueba gratis no es un cobro, así que no vale ni su
  // importe ni el precio del plan: el payload sale sin valor y etiquetado. La
  // página ya corta antes de llegar acá (empuja `trial_starts`), pero el helper
  // no depende de eso: si alguna vez se cuela, se ve en GA4 en lugar de inflar
  // la facturación.
  const trialAuth = isTrialAuthorization(paymentIntent);
  const intentAmount = trialAuth ? undefined : toNumber(paymentIntent?.amount);
  const hasIntentAmount = intentAmount !== undefined && intentAmount > 0;

  let value;
  let valueSource;
  if (trialAuth) {
    valueSource = 'trial_auth';
  } else if (hasIntentAmount) {
    value = intentAmount / CENTS_PER_UNIT;
    valueSource = 'payment_intent';
  } else {
    value = planValue(plan);
    valueSource = value === undefined ? 'missing' : 'plan';
  }

  const currency = hasIntentAmount ? String(paymentIntent.currency || 'usd').toUpperCase() : 'USD';

  const id = planId(plan);
  const name = planName(plan);
  const discount = toNumber(plan?.discount);
  const frequency = plan?.chargeFrecuency;

  return {
    // --- Comercio electrónico canónico de GA4 ---
    transaction_id: transactionId || paymentIntent?.id || undefined,
    value,
    currency,
    payment_type: provider,
    items:
      value === undefined
        ? []
        : [
            {
              item_id: id === undefined ? undefined : String(id),
              item_name: name,
              item_category: frequency,
              price: value,
              quantity: 1
            }
          ],

    // --- Compatibilidad con la etiqueta de GTM publicada hoy ---
    // (lee id, name, discount, originalCost e items desde data.purchase.*)
    id,
    name,
    originalCost: value,
    discount,

    // --- Diagnóstico: permite medir en GA4 cuántas compras llegan sin importe ---
    value_source: valueSource
  };
};

export default buildPurchasePayload;
